import React, { useRef, useState, useCallback, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "../../../contexts/LanguageContext";

/** Images en haute résolution (2x) pour écrans retina et netteté */
const DISHES = [
  {
    nameKey: "topDish1Name",
    descKey: "topDish1Desc",
    rating: 4.8,
    img: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&h=800&fit=crop&q=85",
  },
  {
    nameKey: "topDish2Name",
    descKey: "topDish2Desc",
    rating: 5.0,
    img: "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=800&h=800&fit=crop&q=85",
  },
  {
    nameKey: "topDish3Name",
    descKey: "topDish3Desc",
    rating: 4.6,
    img: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&h=800&fit=crop&q=85",
  },
  {
    nameKey: "topDish4Name",
    descKey: "topDish4Desc",
    rating: 4.9,
    img: "https://images.unsplash.com/photo-1476124369491-e7addf5db371?w=800&h=800&fit=crop&q=85",
  },
  {
    nameKey: "topDish5Name",
    descKey: "topDish5Desc",
    rating: 4.7,
    img: "https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=800&h=800&fit=crop&q=85",
  },
];

/** 3 copies pour boucle continue sans retour visuel */
const EXTENDED_DISHES = [...DISHES, ...DISHES, ...DISHES];
const N = DISHES.length;
const AUTO_PLAY_INTERVAL = 4500;

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const blockVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const cardVariants = {
  hidden: { opacity: 0, x: 24 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      delay: (i % N) * 0.06,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const TopListSection = ({ onViewRecipes }) => {
  const { t } = useLanguage();
  const shouldReduceMotion = useReducedMotion();
  const scrollRef = useRef(null);
  const sectionRef = useRef(null);
  const isJumpingRef = useRef(false);
  const scrollRafRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isInView, setIsInView] = useState(true);

  const getLoopMetrics = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return null;
    const cards = el.querySelectorAll("[data-card]");
    if (cards.length < N * 3) return null;
    const middleStart = cards[N].offsetLeft;
    const middleEnd = cards[N * 2].offsetLeft;
    const loopWidth = middleEnd - middleStart;
    return { el, cards, middleStart, middleEnd, loopWidth };
  }, []);

  /** Position initiale : milieu (2e copie) pour pouvoir boucler dans les deux sens */
  useEffect(() => {
    const init = () => {
      const metrics = getLoopMetrics();
      if (!metrics) return;
      metrics.el.scrollLeft = metrics.middleStart;
    };
    const id = requestAnimationFrame(init);
    return () => cancelAnimationFrame(id);
  }, [getLoopMetrics]);

  /** Pause du défilement auto quand la section est hors de l'écran */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.2, rootMargin: "0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /** Détermine l’index physique (0 à EXTENDED_DISHES.length - 1) le plus visible */
  const getRawIndexFromScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return N;
    const scrollLeft = el.scrollLeft;
    const cards = el.querySelectorAll("[data-card]");
    let best = N;
    let bestDist = Infinity;
    cards.forEach((card, i) => {
      const dist = Math.abs(scrollLeft - card.offsetLeft);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;

      const metrics = getLoopMetrics();
      if (!metrics || isJumpingRef.current) return;
      const { el, middleStart, middleEnd, loopWidth } = metrics;
      const scrollLeft = el.scrollLeft;

      /** Replacer dans la copie du milieu pour boucle continue */
      if (scrollLeft >= middleEnd) {
        isJumpingRef.current = true;
        el.scrollLeft = scrollLeft - loopWidth;
        requestAnimationFrame(() => {
          isJumpingRef.current = false;
        });
      } else if (scrollLeft < middleStart) {
        isJumpingRef.current = true;
        el.scrollLeft = scrollLeft + loopWidth;
        requestAnimationFrame(() => {
          isJumpingRef.current = false;
        });
      }

      const rawIndex = getRawIndexFromScroll();
      const normalizedIndex = rawIndex % N;
      setCurrentIndex((prev) =>
        prev === normalizedIndex ? prev : normalizedIndex,
      );
    });
  }, [getLoopMetrics, getRawIndexFromScroll]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", handleScroll);
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [handleScroll]);

  /** Scroll vers la carte logique (0 à N-1) dans la copie du milieu */
  const goToIndex = useCallback(
    (logicalIndex) => {
      const metrics = getLoopMetrics();
      if (!metrics) return;
      const { el, cards } = metrics;
      const safeIndex = ((logicalIndex % N) + N) % N;
      const middleIndex = N + safeIndex;
      const card = cards[middleIndex];
      if (!card) return;
      el.scrollTo({
        left: card.offsetLeft,
        behavior: shouldReduceMotion ? "auto" : "smooth",
      });
      setCurrentIndex(safeIndex);
    },
    [getLoopMetrics, shouldReduceMotion],
  );

  const goPrev = useCallback(() => {
    const prev = (currentIndex - 1 + N) % N;
    goToIndex(prev);
  }, [currentIndex, goToIndex]);

  const goNext = useCallback(() => {
    const next = (currentIndex + 1) % N;
    goToIndex(next);
  }, [currentIndex, goToIndex]);

  /** Défilement automatique */
  useEffect(() => {
    if (isPaused || !isInView) return;
    const timer = setInterval(() => {
      goNext();
    }, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused, isInView, goNext]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      goPrev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      goNext();
    }
  };

  return (
    <motion.section
      ref={sectionRef}
      className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 w-full min-w-0 overflow-x-hidden"
      aria-label={t("topRecipesTitle")}
      initial={false}
      animate="visible"
      variants={sectionVariants}
    >
      <motion.div
        className="text-center mb-6 sm:mb-8 md:mb-10"
        variants={blockVariants}
      >
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 px-1">
          {t("topRecipesTitle")}
        </h2>
        <p className="text-white text-xs sm:text-sm md:text-base max-w-xl mx-auto px-2">
          {t("topRecipesSubtitle")}
        </p>
      </motion.div>

      <motion.div
        className="flex items-center gap-1.5 sm:gap-3 md:gap-4"
        variants={blockVariants}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocusWithin={() => setIsPaused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setIsPaused(false);
        }}
      >
        <button
          type="button"
          onClick={goPrev}
          aria-label={t("topRecipesPrevAria")}
          className="flex flex-shrink-0 items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-orange-500 text-white hover:bg-orange-600 active:scale-95 touch-manipulation transition-transform"
        >
          ←
        </button>

        <div
          ref={scrollRef}
          role="region"
          aria-roledescription="carousel"
          aria-label={t("topRecipesCarouselAria")}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className={`flex-1 min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-none snap-x snap-mandatory scrollbar-hide focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-xl ${
            shouldReduceMotion ? "" : "scroll-smooth"
          }`}
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorX: "contain",
            scrollPaddingInline: "4px",
          }}
        >
          <div className="flex gap-3 sm:gap-4 md:gap-6 py-1 sm:py-2">
            {EXTENDED_DISHES.map((d, i) => {
              const dishName = t(d.nameKey);
              const dishDesc = t(d.descKey);
              return (
                <motion.div
                  key={`${i}-${d.nameKey}`}
                  data-card
                  custom={i}
                  initial={false}
                  animate="visible"
                  variants={cardVariants}
                  className="w-[72vw] min-w-[220px] max-w-[260px] sm:w-[260px] sm:min-w-[260px] sm:max-w-[280px] md:w-[280px] md:min-w-[280px] md:max-w-[300px] lg:w-[300px] lg:min-w-[300px] flex-shrink-0 snap-center snap-always bg-black/35 sm:bg-white/10 backdrop-blur-none sm:backdrop-blur rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg border border-slate-100/50 sm:border-slate-100"
                  aria-label={dishName}
                >
                  <div className="aspect-square rounded-lg sm:rounded-xl overflow-hidden mb-2 sm:mb-3 bg-slate-100 shadow-inner ring-1 ring-black/5">
                    <img
                      src={d.img}
                      alt={dishName}
                      className="w-full h-full object-cover object-center"
                      loading={i < N ? (i === 0 ? "eager" : "lazy") : "lazy"}
                      decoding="async"
                    />
                  </div>
                  <h3 className="font-bold text-white uppercase text-xs sm:text-sm mb-0.5 sm:mb-1 line-clamp-1">
                    {dishName}
                  </h3>
                  <p className="text-white text-xs mb-2 sm:mb-3 line-clamp-2">
                    {dishDesc}
                  </p>
                  <div className="flex justify-between items-center gap-2">
                    <span
                      className="flex items-center gap-1 font-semibold text-white text-xs sm:text-sm"
                      aria-label={`${d.rating} ${t("topRecipesStarsAria")}`}
                    >
                      <span className="text-amber-400" aria-hidden>
                        ★
                      </span>
                      <span>{d.rating}</span>
                    </span>
                    <button
                      type="button"
                      onClick={onViewRecipes}
                      className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 active:scale-95 text-sm sm:text-base touch-manipulation transition-transform flex-shrink-0"
                      aria-label={`${t("topRecipesViewAria")} ${dishName}`}
                    >
                      +
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={goNext}
          aria-label={t("topRecipesNextAria")}
          className="flex flex-shrink-0 items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-orange-500 text-white hover:bg-orange-600 active:scale-95 touch-manipulation transition-transform"
        >
          →
        </button>
      </motion.div>

      <motion.div
        className="flex justify-center gap-1.5 sm:gap-2 mt-4 sm:mt-6"
        role="tablist"
        aria-label={t("topRecipesPositionAria")}
        variants={blockVariants}
      >
        {DISHES.map((_, i) => (
          <motion.button
            key={i}
            type="button"
            role="tab"
            aria-label={`${t("topRecipesGoToAria")} ${i + 1}`}
            aria-selected={currentIndex === i}
            onClick={() => goToIndex(i)}
            className={`h-2 rounded-full transition-all duration-200 touch-manipulation ${
              currentIndex === i
                ? "w-6 bg-orange-500"
                : "w-2 bg-white/50 hover:bg-white/70"
            }`}
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.95 }}
          />
        ))}
      </motion.div>
    </motion.section>
  );
};

export default TopListSection;
